import { downloadResumePdf } from '@src/api/core/unifiedPdfDownloader';
import { downloadPdf } from '@src/api/pdf';
import { DATA_EXPORT_VERSION } from '@src/constants/version';
import { usePdfToastNotifications } from '@src/hooks/usePdfToastNotifications';
import { selectResumeData } from '@src/selectors/resumeDataSelector';
import type { RootState } from '@src/store/index';
import type { GeneratePdfApiParams } from '@src/types/shared/api/pdf_api_params';
import { getResumePdfDownloadUrl } from '@src/utils/pdfDownloadUrl';
import { useCallback, useState } from 'react';
import { useSelector } from 'react-redux';

type UsePdfDownloadOptions = {
  toastMessages?: {
    pending?: string;
    success?: string;
    error?: string;
  };
  onDownload?: () => void;
};

type UsePdfDownloadReturn = {
  isPdfGenerating: boolean;
  isPdfDownloadSuccess: boolean;
  isPdfDownloadError: boolean;
  handlePdfDownload: () => Promise<void>;
};

export function usePdfDownload(options: UsePdfDownloadOptions = {}): UsePdfDownloadReturn {
  const { toastMessages, onDownload } = options;

  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [isPdfDownloadSuccess, setIsPdfDownloadSuccess] = useState(false);
  const [isPdfDownloadError, setIsPdfDownloadError] = useState(false);

  const resumeData = useSelector(selectResumeData);
  const pdfToken = useSelector((state: RootState) => state.unifiedPdfAccessUrl.resume.token);

  usePdfToastNotifications(
    {
      isPending: isPdfGenerating,
      isSuccess: isPdfDownloadSuccess,
      isError: isPdfDownloadError,
    },
    {
      pending: toastMessages?.pending ?? 'PDFをダウンロードしています',
      success: toastMessages?.success ?? 'PDFのダウンロードが完了しました',
      error: toastMessages?.error ?? 'PDFのダウンロードに失敗しました',
    }
  );

  const handlePdfDownload = useCallback(async (): Promise<void> => {
    if (onDownload) {
      onDownload();
      return;
    }

    setIsPdfDownloadSuccess(false);
    setIsPdfDownloadError(false);
    setIsPdfGenerating(true);

    if (pdfToken) {
      const pdfUrl = getResumePdfDownloadUrl(pdfToken);
      if (pdfUrl) {
        downloadResumePdf(pdfUrl);
        setIsPdfDownloadSuccess(true);
        setIsPdfGenerating(false);
        return;
      }
    }

    if (!resumeData) {
      console.warn('PDF生成に必要なデータが不足しています');
      setIsPdfGenerating(false);
      return;
    }

    try {
      const params: GeneratePdfApiParams = {
        version: DATA_EXPORT_VERSION,
        ...resumeData,
      };
      await downloadPdf(params);
      setIsPdfDownloadSuccess(true);
    } catch (error) {
      console.error('PDF生成エラー:', error);
      setIsPdfDownloadError(true);
    } finally {
      setIsPdfGenerating(false);
    }
  }, [onDownload, pdfToken, resumeData]);

  return {
    isPdfGenerating,
    isPdfDownloadSuccess,
    isPdfDownloadError,
    handlePdfDownload,
  };
}
